import { useState, useEffect, useCallback, Component } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import CommandCentral from '../components/manager/CommandCentral';
import Dashboard from '../components/manager/Dashboard';
import ScheduleManager from '../components/manager/ScheduleManager';
import TaskEditor from '../components/manager/TaskEditor';
import SpecialEventsTracker from '../components/manager/SpecialEventsTracker';
import InvoiceGenerator from '../components/manager/InvoiceGenerator';
import ComplianceCentre from '../components/manager/ComplianceCentre';
import StaffManager from '../components/manager/StaffManager';
import NegativeReports from '../components/manager/NegativeReports';
import WeeklyReport from '../components/manager/WeeklyReport';
import ManagerMessages from '../components/manager/ManagerMessages';
import HelpGuide from '../components/HelpGuide';
import { useVisiblePolling } from '../lib/useVisiblePolling';

// A JS error thrown while rendering any one tab (e.g. a null-reference
// on unexpected data) used to blank the entire manager app — nav bar and
// all — with nothing to click but a browser refresh. This scopes a crash
// to the tab's content area and offers a way back without losing state
// in the rest of the app.
class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('Manager tab crashed:', error, info);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.tabKey !== this.props.tabKey && this.state.error) {
      this.setState({ error: null });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={styles.crashBox}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>Something went wrong loading this tab</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 12 }}>
            Tap "Try Again" below. If it keeps happening, switch to another tab and come back.
          </div>
          <button style={styles.crashRetryBtn} onClick={() => this.setState({ error: null })}>Try Again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const TABS = [
  { id: 'command',     label: 'Command Central', icon: '⌖' },
  { id: 'dashboard',   label: 'Dashboard',    icon: '▣' },
  { id: 'schedule',    label: 'Schedule',     icon: '▦' },
  { id: 'tasks',       label: 'Building Tasks', icon: '☑' },
  { id: 'events',      label: 'Hours Log',    icon: '★' },
  { id: 'invoice',     label: 'Invoicing',    icon: '◈' },
  { id: 'compliance',  label: 'Compliance',   icon: '✓' },
  { id: 'staff',       label: 'Staff',        icon: '◎' },
  { id: 'messages',    label: 'Messages',     icon: '✉' },
  { id: 'reports',     label: 'Complaints',   icon: '⚠' },
  { id: 'weekly',      label: 'Town Report',  icon: '📋' },
  { id: 'help',        label: 'Help',         icon: '?' },
];

export default function ManagerApp() {
  const { profile, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('command');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [openReports, setOpenReports] = useState(0);

  const loadBadgeCounts = useCallback(async () => {
    if (!profile?.id) return;
    const [msgRes, reportRes] = await Promise.all([
      supabase.from('messages').select('id', { count: 'exact', head: true }).eq('recipient_id', profile.id).eq('read', false),
      supabase.from('negative_reports').select('id', { count: 'exact', head: true }).eq('status', 'open'),
    ]);
    if (!msgRes.error) setUnreadMessages(msgRes.count || 0);
    if (!reportRes.error) setOpenReports(reportRes.count || 0);
  }, [profile?.id]);

  // Poll for new unread messages / open reports so a badge can appear without
  // Fabian having to click into those tabs to discover something is waiting.
  useVisiblePolling(loadBadgeCounts, 30000);

  // Refresh immediately on tab switch too, e.g. right after leaving Messages
  // (where opening a thread marks it read) so the badge clears promptly.
  useEffect(() => { loadBadgeCounts(); }, [activeTab, loadBadgeCounts]);

  function renderTab() {
    switch (activeTab) {
      case 'command':    return <CommandCentral />;
      case 'dashboard':  return <Dashboard />;
      case 'schedule':   return <ScheduleManager />;
      case 'tasks':      return <TaskEditor />;
      case 'events':     return <SpecialEventsTracker />;
      case 'invoice':    return <InvoiceGenerator />;
      case 'compliance': return <ComplianceCentre />;
      case 'staff':      return <StaffManager />;
      case 'messages':   return <ManagerMessages />;
      case 'reports':    return <NegativeReports />;
      case 'weekly':     return <WeeklyReport />;
      case 'help':       return <HelpGuide guide="manager" theme="light" />;
      default:           return <CommandCentral />;
    }
  }

  return (
    <div style={styles.shell}>
      {/* Below 720px, a 12-item icon strip only ever shows its first few tabs
          and needs a sideways scroll nobody discovers on their own — swapped
          for a single dropdown that reaches every tab in one tap. */}
      <style>{`
        .tc-tabbar-mobile { display: none; }
        @media (max-width: 720px) {
          .tc-tabbar-desktop { display: none !important; }
          .tc-tabbar-mobile { display: flex !important; }
        }
      `}</style>

      {/* Top nav */}
      <div style={styles.topNav}>
        <div style={styles.navLeft}>
          <div style={styles.logoMark}>TC</div>
          <div>
            <div style={styles.logoName}>TraverseClean</div>
            <div style={styles.logoSub}>Manager · {profile?.full_name || 'Fabian Traverse'}</div>
          </div>
        </div>
        <button style={styles.signOutBtn} onClick={signOut}>Sign Out</button>
      </div>

      {/* Tab bar — desktop/tablet */}
      <div className="tc-tabbar-desktop" style={styles.tabBar}>
        {TABS.map(tab => {
          const badgeCount = tab.id === 'messages' ? unreadMessages : tab.id === 'reports' ? openReports : 0;
          return (
            <button
              key={tab.id}
              style={activeTab === tab.id ? { ...styles.tab, ...styles.tabActive } : styles.tab}
              onClick={() => setActiveTab(tab.id)}
            >
              <span style={styles.tabIconWrap}>
                <span style={styles.tabIcon}>{tab.icon}</span>
                {badgeCount > 0 && <span style={styles.tabBadge}>{badgeCount > 99 ? '99+' : badgeCount}</span>}
              </span>
              <span style={styles.tabLabel}>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab bar — phone: every tab reachable in one tap, none hidden off-screen */}
      <div className="tc-tabbar-mobile" style={styles.tabBarMobile}>
        <select
          style={styles.tabSelect}
          value={activeTab}
          onChange={e => setActiveTab(e.target.value)}
        >
          {TABS.map(tab => {
            const badgeCount = tab.id === 'messages' ? unreadMessages : tab.id === 'reports' ? openReports : 0;
            return (
              <option key={tab.id} value={tab.id}>
                {tab.icon}  {tab.label}{badgeCount > 0 ? `  (${badgeCount > 99 ? '99+' : badgeCount})` : ''}
              </option>
            );
          })}
        </select>
      </div>

      {/* Content */}
      <div style={styles.content}>
        <TabErrorBoundary tabKey={activeTab}>
          {renderTab()}
        </TabErrorBoundary>
      </div>
    </div>
  );
}

const styles = {
  shell: {
    minHeight: '100vh',
    background: '#0f172a',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },
  topNav: {
    background: '#1e3a5f',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #334155',
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logoMark: {
    background: '#f97316',
    color: 'white',
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 13,
    flexShrink: 0,
  },
  logoName: {
    color: 'white',
    fontWeight: 700,
    fontSize: 16,
    letterSpacing: -0.3,
  },
  logoSub: {
    color: '#94a3b8',
    fontSize: 11,
  },
  signOutBtn: {
    background: 'transparent',
    border: '1px solid #334155',
    color: '#64748b',
    padding: '6px 14px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
  },
  tabBar: {
    background: '#1e293b',
    display: 'flex',
    borderBottom: '1px solid #334155',
    overflowX: 'auto',
  },
  tabBarMobile: {
    background: '#1e293b',
    borderBottom: '1px solid #334155',
    padding: '10px 14px',
  },
  tabSelect: {
    width: '100%',
    background: '#0f172a',
    border: '1px solid #334155',
    color: '#f1f5f9',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 15,
    fontWeight: 600,
  },
  tab: {
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    minWidth: 72,
    borderBottom: '2px solid transparent',
    transition: 'all 0.15s',
    flexShrink: 0,
  },
  tabActive: {
    color: '#f97316',
    borderBottom: '2px solid #f97316',
    background: '#0f172a22',
  },
  tabIconWrap: {
    position: 'relative',
    display: 'inline-flex',
  },
  tabIcon: {
    fontSize: 16,
  },
  tabBadge: {
    position: 'absolute',
    top: -6,
    right: -10,
    background: '#ef4444',
    color: 'white',
    fontSize: 9,
    fontWeight: 800,
    borderRadius: 8,
    padding: '1px 4px',
    minWidth: 14,
    textAlign: 'center',
    lineHeight: '12px',
  },
  tabLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    whiteSpace: 'nowrap',
  },
  content: {
    flex: 1,
    // No overflow here on purpose. The shell is minHeight rather than height,
    // so this box grows with its content and the window does the scrolling —
    // this never scrolled itself. But an ancestor with overflow set still
    // captures position: sticky, which anchored the Schedule page's week bar
    // to a box that never moves, so it silently did nothing.
  },
  crashBox: {
    margin: 20,
    padding: '20px 24px',
    background: '#1c0505',
    border: '1px solid #7f1d1d',
    borderRadius: 12,
    color: '#fca5a5',
  },
  crashRetryBtn: {
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
};
